import assert from '@/sui/tools/assert'
import rtti from '@/sui/tools/rtti'
import moment from 'moment'

export function strIndex (i, padCount = 3) {
  assert.isNumber(i)
  assert(i < Math.pow(10, padCount))
  return String(i).padStart(padCount, '0')
}

const cache = new Map()
export function validDate (date) {
  if (date == null || date === '' || date[0] === '0') {
    return null
  }

  return date
}

export function newDate (date) {
  return newDateFmt(date, 'DD/MM/YYYY')
}

export function newDateFmt (date, fmt) {
  if (validDate(date) == null) {
    return null
  }
  if (fmt == null) {
    return null
  }

  const cached = cache.get(date)
  if (cached != null) {
    return cached
  }
  const str = moment(new Date(date)).format(fmt)
  cache.set(date, str)
  return str
}

export function duration (f, s) {
  const ff = new Date(f)
  const fs = new Date(s)
  if (ff.getFullYear() === 1 || fs.getFullYear() === 1) {
    return null
  }

  return moment(ff).diff(moment(fs), 'days')
}

export function durationFunctor (f, s) {
  return (_, r) => {
    return duration(r[f], r[s])
  }
}

export function score (score, field) {
  if (field.code === score) {
    return field.value
  }
  return null
}

export function valueOrNull (value, test) {
  return test ? value : null
}

export function getFields (config, prefix, target, value) {
  const len = config.length
  for (let i = 0; i < len; ++i) {
    const c = config[i]
    const index = strIndex(i)
    target[`${prefix}_${index}_${c.key}`] = c.get(value)
  }
}

function addColumn (columns, column, removeEmptyColumns, sheets) {
  if (!removeEmptyColumns) {
    columns.push(column)
    return
  }

  addColumnIfNotEmpty(columns, column, sheets)
}

function addColumnIfNotEmpty (columns, column, sheets) {
  for (let i = 0; i !== sheets.length; ++i) {
    const sheet = sheets[i]
    const value = sheet[column.key]
    if (value != null && (value.length == null || value.length > 0)) {
      columns.push(column)
      break
    }
  }
}

function addColumns (columns, keys, removeEmptyColumns, sheet) {
  const keyList = Array.from(keys).sort()
  for (let i = 0; i !== keyList.length; ++i) {
    const key = keyList[i]
    const csvKey = key.replace(/^_[0-9]+_/, '').replace(/_[0-9]+_/g, '_')
    const column = { header: csvKey, key: key }
    addColumn(columns, column, removeEmptyColumns, sheet)
  }
}

export function extractKeys (result, patient, prefixes) {
  Object.keys(patient).forEach(
    k => {
      prefixes.forEach(
        p => {
          if (k.match(`^${p}[01]`)) {
            result[p].add(k)
          }
        }
      )
    }
  )
}

export function convert (params, reducer, sets) {
  const start = {
    di: 0,
    ti: 0,
    si: 0,
    i: 0,
    r: [],
    p: params,
    cache: {}
  }
  sets.forEach(s => { start[s] = new Set() })
  start.r.length = params.sheets.length
  const result = params.sheets.reduce(reducer, start)
  const columns = []
  for (let i = 0; i !== result.keys.length; ++i) {
    const column = { key: result.keys[i] }
    addColumn(columns, column, params.removeEmptyColumns, result.r)
  }

  for (let key in sets) {
    const set = sets[key]
    addColumns(columns, result[set], params.removeEmptyColumns, result.r)
  }

  return {
    sheets: result.r,
    options: {
      header: true,
      bom: true,
      delimiter: ';',
      columns: columns
    }
  }
}

export function convertEcdc (params, reducer) {
  const start = {
    di: 0,
    ti: 0,
    si: 0,
    i: 0,
    r: [],
    p: params,
    cache: {}
  }
  const result = params.sheets.reduce(reducer, start)
  const columns = []

  if (result.r.length > 0) {
    Object.keys(result.r[0]).forEach(k => columns.push({ key: k }))
  }
  return {
    sheets: result.r,
    options: {
      header: true,
      bom: true,
      delimiter: ';',
      columns: columns
    }
  }
}

function getFormattedField (content, item, field, year) {
  if (content == null) {
    return null
  }

  if (item.type === 'list') {
    const result = item.config.values.find(
      v => v.index != null ? v.index === content : v.code === content
    )
    return result.value
  }

  if (item.type === 'bool') {
    return content ? 'Oui' : null
  }

  if (item.type === 'date') {
    return newDate(content)
  }

  return content
}

export function flattenSheetFromJSTree (initialize, tree, p, s, keySet) {
  assert.isFunction(initialize)
  assert.notNull(tree)
  assert.isObject(p)
  const sheet = p.f
  assert.notNull(sheet)

  const { result, initCount } = initialize(s, p, keySet)

  assert.isObject(result)

  const stack = [
    {
      treeFields: tree,
      sheetFields: [sheet],
      prefix: '',
      exclusive: false,
      startIndex: initCount
    }
  ]

  while (stack.length > 0) {
    const {
      treeFields,
      sheetFields,
      prefix,
      startIndex,
      exclusive
    } = stack.pop()
    assert.isArray(sheetFields)
    assert.isArray(treeFields)
    assert.isString(prefix)

    for (let i = 0; i !== sheetFields.length; ++i) {
      const shf = sheetFields[i]
      assert.isObject(shf)

      let shfPrefix = ''
      for (let j = 0; j !== treeFields.length; ++j) {
        const item = treeFields[j]
        const config = item.config
        const convert = item.convert
        if (convert.special === 'none') {
          continue
        }

        const key = convert.server != null ? convert.server : item.field
        const value = shf[key]

        if (item.type === 'array') {
          assert(prefix === '' || shfPrefix !== '')
          assert.isArray(value)
          if (value.length === 0) {
            continue
          }

          const arrayPrefix = config.csvArrayPrefix
          assert.notNull(arrayPrefix)
          assert.isString(arrayPrefix)
          assert(arrayPrefix.length > 0)

          const csvFieldName = `${shfPrefix}_${strIndex(startIndex + j)}_${config.csvArrayPrefix}`
          stack.push({
            treeFields: config.fields,
            sheetFields: value,
            prefix: csvFieldName,
            exclusive: config.exclusive,
            startIndex: 0
          })
          continue
        }

        if (item.type === 'pickList') {
          assert(prefix === '' || shfPrefix !== '')
          if (value == null) {
            continue
          }

          assert.isArray(value)
          const choices = item.config.values
          assert(choices.length === value.length)
          for (let k = 0; k !== choices.length; ++k) {
            const choice = choices[k]
            const v = value[k]
            assert(v.key === choice.code)
            const csvValue = v.value ? 'Oui' : null
            const csvFieldName = `${shfPrefix}_${strIndex(startIndex + j)}_${config.csvField}_${strIndex(k + 1, 2)}_${choice.value}`
            keySet.add(csvFieldName)
            result[csvFieldName] = csvValue
          }

          continue
        }

        const csvValue = getFormattedField(value, item, key, s.year)
        if (j === 0 && prefix !== '') {
          let separator = '_N°'
          let suffix = i + 1
          if (exclusive) {
            suffix = value

            if (!rtti.isNumber(suffix)) {
              const index = item.config.values.findIndex(
                v => v.index == null ? v.code === value : v.index === value
              )
              separator = `_${strIndex(index)}_`
            }
          }

          if (rtti.isNumber(suffix)) {
            suffix = strIndex(suffix, 2)
          }

          shfPrefix = `${prefix}${separator}${suffix}`
        }
        assert(prefix === '' || shfPrefix !== '')

        const csvFieldName = `${shfPrefix}_${strIndex(startIndex + j)}_${config.csvField}`
        keySet.add(csvFieldName)
        result[csvFieldName] = csvValue
      }
    }
  }

  return result
}
